import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import PublicNav from "@/components/PublicNav";
import {
  Globe, ShieldCheck, Zap, Users, Infinity as InfinityIcon,
  ArrowRight, MapPin, Search, Briefcase, UserCheck, FileText, Bell,
} from "lucide-react";

const FEATURES = [
  { icon: Globe, title: "Talent mondial", desc: "Des opportunités dans tous les pays" },
  { icon: ShieldCheck, title: "Processus sécurisé", desc: "Des profils vérifiés et fiables" },
  { icon: Zap, title: "Recrutement rapide", desc: "Gagnez du temps, trouvez le bon talent" },
  { icon: Users, title: "Pour tous les secteurs", desc: "Des métiers variés, des talents uniques" },
  { icon: InfinityIcon, title: "Un avenir sans frontières", desc: "Ensemble pour un monde du travail plus ouvert" },
];

const CANDIDATE_STEPS = [
  { icon: UserCheck, text: "Créez votre profil professionnel en quelques minutes" },
  { icon: FileText, text: "Téléversez votre CV et indiquez vos préférences" },
  { icon: Search, text: "Recevez des recommandations et postulez directement" },
  { icon: Bell, text: "Suivez vos candidatures et recevez des notifications" },
];

const EMPLOYER_STEPS = [
  { icon: Briefcase, text: "Publiez vos offres d'emploi en quelques clics" },
  { icon: Search, text: "Recherchez dans la banque internationale de talents" },
  { icon: Users, text: "Gérez vos candidatures et organisez vos entrevues" },
  { icon: Globe, text: "Recrutez partout dans le monde, en toute simplicité" },
];

export default function Landing() {
  const { user } = useAuth();
  const location = useLocation();
  const candidateCta = user ? "/dashboard" : "/register?role=candidat";
  const employerCta = user ? "/dashboard" : "/register?role=employeur";

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) {
        const t = setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 150);
        return () => clearTimeout(t);
      }
    }
  }, [location.hash]);

  return (
    <div data-testid="landing-page" className="min-h-screen bg-white">
      <PublicNav />

      {/* Hero */}
      <section className="bg-white pt-14 pb-0">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-brand-turquoise mb-4">
            NEXA — International Recruitment Platform
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-brand-navy leading-tight mb-4">
            Le talent n'a pas de <span className="text-brand-turquoise">frontières</span>
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto mb-6">
            La plateforme de recrutement international qui connecte les talents aux opportunités.
          </p>
          <a
            data-testid="landing-lcs-badge"
            href="https://www.lcsimmigration.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-white border border-slate-200 rounded-full px-5 py-2.5 mb-12 shadow-sm hover:border-brand-turquoise transition-colors"
          >
            <img src="/lcs-immigration-logo.png" alt="LCS Immigration" className="h-9 w-auto" />
            <span className="text-xs text-slate-500 text-left leading-tight">
              NEXA est un projet de <span className="font-semibold text-brand-navy">LCS Immigration</span><br />
              Le Groupe Conseil Services Immigration
            </span>
          </a>
        </div>

        {/* Two paths */}
        <div className="grid md:grid-cols-2">
          <div id="candidats" className="relative bg-brand-navy text-white px-8 py-16 lg:px-20 lg:py-24 overflow-hidden">
            <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-brand-turquoise/10" />
            <div className="absolute bottom-10 left-1/3 w-40 h-40 rounded-full bg-brand-blue/10" />
            <div className="relative max-w-md">
              <h2 className="text-3xl font-bold mb-1">Vous êtes</h2>
              <h2 className="text-3xl font-bold text-brand-turquoise mb-5">candidat ?</h2>
              <p className="text-white/70 mb-8">
                Trouvez des opportunités internationales et donnez un nouvel élan à votre carrière.
              </p>
              <Link
                data-testid="landing-cta-candidate"
                to={candidateCta}
                className="inline-flex items-center gap-2 bg-brand-turquoise text-white font-semibold rounded-lg px-7 py-3.5 hover:bg-white hover:text-brand-navy transition-colors duration-200"
              >
                Créer mon profil
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div id="employeurs" className="relative bg-[#0E355F] text-white px-8 py-16 lg:px-20 lg:py-24 overflow-hidden">
            <div className="absolute -bottom-24 -left-16 w-80 h-80 rounded-full bg-brand-orange/10" />
            <div className="relative max-w-md ml-auto">
              <h2 className="text-3xl font-bold mb-1">Vous êtes</h2>
              <h2 className="text-3xl font-bold text-brand-blue mb-5">employeur ?</h2>
              <p className="text-white/70 mb-8">
                Trouvez les talents dont vous avez besoin partout dans le monde.
              </p>
              <Link
                data-testid="landing-cta-employer"
                to={employerCta}
                className="inline-flex items-center gap-2 bg-brand-blue text-white font-semibold rounded-lg px-7 py-3.5 hover:bg-white hover:text-brand-navy transition-colors duration-200"
              >
                Publier une offre
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features strip */}
      <section className="bg-white py-14 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-5 gap-8">
          {FEATURES.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="text-center">
              <Icon className="w-8 h-8 text-brand-blue mx-auto mb-3" strokeWidth={1.5} />
              <p className="font-semibold text-brand-navy text-sm mb-1">{title}</p>
              <p className="text-xs text-slate-500">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works — candidates */}
      <section className="py-20 bg-brand-sky/40">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-semibold tracking-[0.25em] uppercase text-brand-turquoise mb-3">Pour les candidats</p>
            <h2 className="text-3xl font-bold text-brand-navy mb-4">
              Votre carrière internationale commence ici
            </h2>
            <p className="text-slate-500 mb-8">
              Créez un profil complet, téléversez votre CV, indiquez vos compétences et les pays
              recherchés. Les employeurs et recruteurs pourront vous trouver.
            </p>
            <Link
              data-testid="landing-candidate-secondary-cta"
              to={candidateCta}
              className="inline-flex items-center gap-2 text-brand-turquoise font-semibold hover:gap-3 transition-all duration-200"
            >
              Créer mon profil gratuitement <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="space-y-4">
            {CANDIDATE_STEPS.map(({ icon: Icon, text }, i) => (
              <div key={i} className="flex items-center gap-4 bg-white rounded-xl border border-slate-100 p-4 shadow-sm">
                <span className="w-10 h-10 rounded-lg bg-brand-turquoise/10 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-brand-turquoise" />
                </span>
                <p className="text-sm font-medium text-brand-navy">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works — employers + talent pool */}
      <section id="talent" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-4 order-2 lg:order-1">
            {EMPLOYER_STEPS.map(({ icon: Icon, text }, i) => (
              <div key={i} className="flex items-center gap-4 bg-brand-sky/50 rounded-xl border border-brand-blue/10 p-4">
                <span className="w-10 h-10 rounded-lg bg-brand-blue/10 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-brand-blue" />
                </span>
                <p className="text-sm font-medium text-brand-navy">{text}</p>
              </div>
            ))}
          </div>
          <div className="order-1 lg:order-2">
            <p className="text-xs font-semibold tracking-[0.25em] uppercase text-brand-blue mb-3">Pour les employeurs</p>
            <h2 className="text-3xl font-bold text-brand-navy mb-4">
              Recrutez les meilleurs talents, partout dans le monde
            </h2>
            <p className="text-slate-500 mb-8">
              Publiez vos offres, accédez au Talent Pool international, gérez vos candidatures
              et suivez votre recrutement dans un pipeline intégré.
            </p>
            <Link
              data-testid="landing-employer-secondary-cta"
              to={employerCta}
              className="inline-flex items-center gap-2 text-brand-blue font-semibold hover:gap-3 transition-all duration-200"
            >
              Publier une offre <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Ressources / À propos */}
      <section id="ressources" className="py-20 bg-brand-sky/40">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-brand-turquoise mb-3">Ressources</p>
          <h2 className="text-3xl font-bold text-brand-navy mb-10">Explorez les opportunités</h2>
          <div className="grid md:grid-cols-3 gap-6 text-left">
            <Link to="/jobs" data-testid="landing-browse-jobs" className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm hover:shadow-md transition-shadow duration-200 group">
              <Search className="w-7 h-7 text-brand-turquoise mb-4" />
              <p className="font-semibold text-brand-navy mb-1 group-hover:text-brand-turquoise transition-colors">Offres d'emploi</p>
              <p className="text-sm text-slate-500">Parcourez les postes ouverts à l'international.</p>
            </Link>
            <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm">
              <MapPin className="w-7 h-7 text-brand-blue mb-4" />
              <p className="font-semibold text-brand-navy mb-1">Pays de recrutement</p>
              <p className="text-sm text-slate-500">Canada, Europe, Afrique du Nord et plus.</p>
            </div>
            <div id="apropos" className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm">
              <Globe className="w-7 h-7 text-brand-orange mb-4" />
              <p className="font-semibold text-brand-navy mb-1">À propos</p>
              <p className="text-sm text-slate-500">
                NEXA connecte talents et entreprises au-delà des frontières, avec un
                processus humain, sécurisé et transparent.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-10">
          <p className="text-center text-3xl font-light italic text-white/90 mb-12">
            Le monde recrute, <span className="text-brand-orange not-italic font-semibold">et vous ?</span>
          </p>
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 border-t border-white/10 pt-8">
            <span className="text-lg font-extrabold">
              Global<span className="text-brand-turquoise">Talent</span>
            </span>
            <p className="text-sm text-white/50">Le talent n'a pas de frontières</p>
            <a
              data-testid="footer-lcs-link"
              href="https://www.lcsimmigration.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <img src="/lcs-immigration-logo.png" alt="LCS Immigration" className="h-8 w-auto bg-white rounded px-1.5 py-0.5" />
              <span className="text-xs text-white/50">Un projet de LCS Immigration</span>
            </a>
            <p className="text-xs text-white/40">© 2026 NEXA — International Recruitment Platform</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
